import './FilterPopUp.scss';
import { useCallback, useRef, PropsWithChildren, useEffect, useState } from 'react';
import { Popup, ToolbarItem } from 'devextreme-react/popup';
import ValidationGroup from 'devextreme-react/validation-group';
import { useScreenSize } from '../../../utils/media-query';
import { Button } from 'devextreme-react';
import Form, { Item, Label } from 'devextreme-react/form';
import DropDownBox from 'devextreme-react/drop-down-box';
import { useAppSelector, useAppDispatch, selectModuleTracking, selectModuleLocation } from '../../../contexts/hooks';
import { LIS_States } from '../../../utils/systemTypes';
import { FilteredOrders } from '../../../types/types.orders';
import { useApiOrders } from '../../../api/api.orders';
import { setFilteredOrders, setFilteresToOrders, setFiltersToOrdersText, setPanelVisible } from '../../../contexts/slices/orders';
import { ConvertLocalDate, generateFiltersText } from '../../../utils/functions';
import { useFilterStudiesDropDown } from './hooks/useFilterStudiesDropDown';
import { FilterStudiesDataGrid } from './components/FilterStudiesDataGrid';
import { shallowEqual } from 'react-redux';

type PopupProps = {
  visible: boolean;
  setVisible: () => void;
};
const today = new Date().toISOString();
const FieldsToFilters: FilteredOrders = {
  Fechas: [today, today],
};

export const FilterPopup = ({ visible, setVisible }: PropsWithChildren<PopupProps>) => {
  const { isXSmall } = useScreenSize();
  const [loading, setLoading] = useState(false);
  const width = isXSmall ? 360 : 600;
  const frmRef = useRef<Form>(null);
  const refDropDownStudies = useRef<DropDownBox>(null);
  const LstEstudios = useAppSelector((x) => x.configuration.Data?.Estudios);
  const LstAreas = useAppSelector((x) => x.configuration.Data?.Areas);
  const LstServicios = useAppSelector((x) => x.configuration.Data?.ComboBox)?.filter((x) => x.Name === 'SysA_Servicios')[0].Data;
  const LstSedes = useAppSelector((x) => x.configuration.Data?.ComboBox)?.filter((x) => x.Name === 'SysI_Sedes')[0].Data;
  const CurrentFilters = useAppSelector((x) => x.globalOrders.FiltersToOrders);
  const dispatch = useAppDispatch();
  const { GetOrdersByFilter } = useApiOrders();
  const Prm_Ord_ViewOtherLocation = useAppSelector((x) => x.userInfo.Permissions?.Prm_Ord_ViewOtherLocation);
  
  // Usar selectores memoizados
  const ModuleTracking = useAppSelector(selectModuleTracking);
  const ModuleLocation = useAppSelector(selectModuleLocation);
  // Estado para el dropdown de estudios
  const [selectedStudios, setSelectedStudios] = useState<number[]>([]);

  // Custom hook para el DropDownBox de estudios
  const handleStudiosChange = useCallback((estudiosIds: number[]) => {
    setSelectedStudios(estudiosIds);
  }, []);

  const filterStudiesDropDown = useFilterStudiesDropDown(handleStudiosChange);


  const validationGroup = useRef<ValidationGroup>(null);
  useEffect(() => {
    if (visible) {
      validationGroup.current?.instance.reset();
    }
  }, [visible]);

  const close = () => {
    validationGroup.current?.instance.reset();
    setVisible();
  };

  const onCancelClick = useCallback(() => {
    close();
  }, [close, validationGroup]);

  const handleConfirm = useCallback(() => {
    if (frmRef.current?.instance) {
      // Obtener todos los valores del formulario
      const fechas = frmRef.current.instance.getEditor('Fechas')?.option('value');
      const orden = frmRef.current.instance.getEditor('Orden')?.option('value');
      const paciente = frmRef.current.instance.getEditor('Paciente')?.option('value');
      const areas = frmRef.current.instance.getEditor('Areas')?.option('value');
      const servicios = frmRef.current.instance.getEditor('Servicios')?.option('value');
      const estados = frmRef.current.instance.getEditor('Estados')?.option('value');
      const sedes = frmRef.current.instance.getEditor('Sedes')?.option('value');
      const tracking = frmRef.current.instance.getEditor('Tracking')?.option('value');

      // Validación: Si no hay fechas, debe haber al menos Orden o Paciente
      const hasFechas = fechas && Array.isArray(fechas) && fechas.length === 2 && fechas[0] && fechas[1];
      const hasOrden = orden && orden.trim() !== '';
      const hasPaciente = paciente && paciente.trim() !== '';

      if (!hasFechas && !hasOrden && !hasPaciente) {
        // Mostrar error de validación
        const fechasEditor = frmRef.current.instance.getEditor('Fechas');
        const ordenEditor = frmRef.current.instance.getEditor('Orden');
        const pacienteEditor = frmRef.current.instance.getEditor('Paciente');

        if (fechasEditor) fechasEditor.option('isValid', false);
        if (ordenEditor) ordenEditor.option('isValid', false);
        if (pacienteEditor) pacienteEditor.option('isValid', false);

        return;
      }

      setLoading(true);

      const fechasConvertidas = hasFechas ? fechas.map((fecha: Date) => ConvertLocalDate(new Date(fecha))) : [];
      const updatedFieldsToFilters: FilteredOrders = {
        Fechas: fechasConvertidas,
        Orden: orden || '',
        Paciente: paciente || '',
        Areas: areas || [],
        Estudios: selectedStudios || [], // Usar el estado local en lugar del editor del formulario
        Servicios: servicios || [],
        Estados: estados || [],
        Sedes: sedes || [],
        Tracking: tracking !== undefined ? tracking : undefined,
        Redirect: false, // No redirigir automáticamente desde el popup
      };      
      GetOrdersByFilter(updatedFieldsToFilters)
        .then((res) => {      
          dispatch(setFilteredOrders(res));
          dispatch(setFilteresToOrders(updatedFieldsToFilters));

          // Generar y guardar el texto de los filtros
          const filtersText = generateFiltersText(updatedFieldsToFilters, LstAreas, LstEstudios, LstServicios, LIS_States, LstSedes);
          dispatch(setFiltersToOrdersText(filtersText));

          dispatch(setPanelVisible(true));
          close();
        })
        .catch((error) => {
          console.error('Error al obtener órdenes filtradas:', error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [selectedStudios, GetOrdersByFilter, dispatch, close, generateFiltersText]);

  useEffect(() => {
    if (!visible) {
      frmRef.current?.instance.updateData(CurrentFilters);
    } else {
      frmRef.current?.instance.beginUpdate();
    }
  }, [visible, CurrentFilters]);

  // // Efecto para actualizar automáticamente las órdenes cuando cambien los filtros desde el TagBox
  // useEffect(() => {
  //   const currentEstados = CurrentFilters?.Estados || [];
  //   if (CurrentFilters && !visible && CurrentFilters.Fechas && CurrentFilters.Fechas.length > 0 && previousEstados.current.length >= 0 && JSON.stringify(currentEstados) !== JSON.stringify(previousEstados.current)) {
  //     setLoading(true);
  //     alert("Filterpopup")
  //     GetOrdersByFilter(CurrentFilters)
  //       .then((res) => {
  //         dispatch(setFilteredOrders(res));

  //         // Actualizar también el texto de los filtros
  //         const filtersText = generateFiltersText(CurrentFilters);
  //         dispatch(setFiltersToOrdersText(filtersText));

  //         dispatch(setPanelVisible(true));
  //       })
  //       .catch((error) => {
  //         console.error('Error al actualizar órdenes filtradas:', error);
  //       })
  //       .finally(() => {
  //         setLoading(false);
  //       });
  //   }

  //   // Actualizar la referencia anterior
  //   previousEstados.current = [...currentEstados];
  // }, [CurrentFilters?.Estados, visible, GetOrdersByFilter, dispatch, generateFiltersText]); // Solo escuchar cambios en Estados

  const handleCancel = useCallback(() => {
    frmRef.current?.instance.resetValues();
    setVisible();
  }, [FieldsToFilters]);

  return (
    <Popup title='Filtrar órdenes' visible={visible} showCloseButton={true} onHiding={onCancelClick} fullScreen={isXSmall} width={width} wrapperAttr={{ class: `form-popup` }} height='auto'>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleConfirm();
        }}>
        <Form ref={frmRef} formData={FieldsToFilters}>
          <Item dataField='Fechas' editorType='dxDateRangeBox' editorOptions={{ displayFormat: 'shortdate', showClearButton: true }}>
            <Label text='Fecha' />
          </Item>
          <Item dataField='Orden'>
            <Label text='Orden' />
          </Item>
          <Item dataField='Paciente'>
            <Label text='Paciente' />
          </Item>
          <Item dataField='Areas' editorOptions={{ items: LstAreas, showSelectionControls: true, showClearButton: true, showDataBeforeSearch: true, minSearchLength: 2, searchTimeout: 200, searchEnabled: true, displayExpr: 'Aa_Nombre', valueExpr: 'Aa_Id' }} editorType='dxTagBox'>
            <Label text='Área' />
          </Item>
          <Item
            dataField='Estudios'
            cssClass='full-width-item'
            render={() => (
              <DropDownBox
                value={filterStudiesDropDown.studiesDropDownValue}
                opened={filterStudiesDropDown.isStudiesDropDownOpened}
                valueExpr='Es_Id'
                deferRendering={false}
                displayExpr={filterStudiesDropDown.studiesDisplayExpr}
                placeholder='Seleccionar estudio...'
                showClearButton={true}
                stylingMode='outlined'
                className='comboboxItems'
                focusStateEnabled={true}
                width='100%'
                ref={refDropDownStudies}
                dataSource={filterStudiesDropDown.studiesDataSource}
                onValueChanged={filterStudiesDropDown.syncStudiesSelection}
                onOptionChanged={filterStudiesDropDown.onStudiesDropDownOpened}
                onKeyDown={filterStudiesDropDown.onStudiesKeyDown}
                contentRender={() => (
                  <FilterStudiesDataGrid
                    studiesDataSource={filterStudiesDropDown.studiesDataSource}
                    studiesDropDownValue={filterStudiesDropDown.studiesDropDownValue}
                    onStudiesSelectionChanged={filterStudiesDropDown.onStudiesSelectionChanged}
                    onStudiesRowDblClick={filterStudiesDropDown.onStudiesRowDblClick}
                    studiesSearchValue={filterStudiesDropDown.studiesSearchValue}
                    onStudiesSearchValueChanged={filterStudiesDropDown.onStudiesSearchValueChanged}
                    onStudiesKeyDown={filterStudiesDropDown.onStudiesKeyDown}
                    setIsStudiesDropDownOpened={filterStudiesDropDown.setIsStudiesDropDownOpened}
                    refDropDownStudies={refDropDownStudies}
                  />
                )}
                dropDownOptions={{
                  width: isXSmall ? '100vw' : 700,
                  height: isXSmall ? '100vh' : 500,
                  showTitle: false,
                  shading: false,
                  closeOnOutsideClick: !isXSmall,
                }}
              />
            )}>
            <Label text='Estudio' />
          </Item>
          <Item dataField='Servicios' visible={LstServicios && LstServicios.length > 0} editorOptions={{ items: LstServicios, showSelectionControls: true, displayExpr: 'Se_Nombre', valueExpr: 'Se_Id' }} editorType='dxTagBox'>
            <Label text='Servicio' />
          </Item>
          <Item dataField='Sedes' visible={LstSedes && LstSedes.length > 0 && ModuleLocation && Prm_Ord_ViewOtherLocation} editorOptions={{ items: LstSedes, showSelectionControls: true, displayExpr: 'Sd_Nombre', valueExpr: 'Sd_Id' }} editorType='dxTagBox'>
            <Label text='Sede' />
          </Item>
          <Item dataField='Estados' editorOptions={{ items: LIS_States, showSelectionControls: true, displayExpr: 'State', valueExpr: 'Value' }} editorType='dxTagBox'>
            <Label text='Estado' />
          </Item>
          <Item 
            dataField='Tracking' 
            visible={ModuleTracking} 
            editorType='dxSelectBox'
            editorOptions={{ 
              items: [
                { text: 'Todas', value: undefined },
                { text: 'Con tracking', value: true },
                { text: 'Sin tracking', value: false }
              ],
              displayExpr: 'text',
              valueExpr: 'value',
              showClearButton: true,
              stylingMode: 'outlined'
            }}
          >
            <Label text='Tracking' />
          </Item>
        </Form>
        <button type='submit' style={{ display: 'none' }} aria-hidden />
      </form>
      <ToolbarItem toolbar='bottom' location='after'>
        <div className={`form-popup-buttons-container`}>
          <Button text='Cancelar' stylingMode='contained' onClick={handleCancel} />
          <Button text='Confirmar' disabled={loading} stylingMode='contained' type='default' onClick={handleConfirm} />
        </div>
      </ToolbarItem>
    </Popup>
  );
};
